import { useEffect, useRef } from "react";

/**
 * Excel-style click + drag rectangular cell selection across data-entry grids.
 *
 *   mousedown on a grid input        → record anchor cell
 *   mousemove onto a different input → enter DRAG mode, highlight rectangular
 *                                       range between anchor and current cell
 *   mouseup                          → exit drag mode (selection persists)
 *   Click (no drag)                  → clear previous selection
 *   Esc                              → clear selection
 *   Ctrl/Cmd + C  (selection ≥ 2)    → build TSV from selected inputs, write
 *                                       to clipboard, toast "Copied N cells"
 *   Ctrl/Cmd + V                     → on paste event, if clipboard is multi-row
 *                                       OR multi-col, do rectangular paste from
 *                                       the focused input. Inputs marked
 *                                       data-skip-grid-paste="true" are skipped
 *                                       so existing per-cell onPaste handlers
 *                                       keep their own validation (e.g. Daily
 *                                       Entry pack-size limits).
 *
 * Eligibility rules mirror useGridEnterNavigation.js — leaves the login form,
 * Radix popovers / dialogs / comboboxes, non-text input types, and explicit
 * opt-outs (data-skip-grid-nav) alone.
 */

const SKIP_TYPES = new Set([
  "password", "email", "search", "submit", "button", "file",
  "checkbox", "radio", "hidden", "date", "time", "datetime-local",
  "month", "week", "color", "range", "image", "reset",
]);

const SKIP_ANCESTOR_SELECTOR = [
  "form",
  "[role='dialog']",
  "[role='combobox']",
  "[role='listbox']",
  "[role='menu']",
  "[data-radix-popper-content-wrapper]",
  '[data-state="open"][data-side]',
].join(",");

const ROW_TOLERANCE_PX = 10;
const SEL_CLASS = "cyl-cell-selected";
const ANCHOR_CLASS = "cyl-cell-anchor";

function isGridInput(el) {
  if (!el || el.tagName !== "INPUT") return false;
  if (el.disabled) return false;
  const t = (el.type || "text").toLowerCase();
  if (SKIP_TYPES.has(t)) return false;
  if (el.closest(SKIP_ANCESTOR_SELECTOR)) return false;
  if (el.offsetParent === null && el.getClientRects().length === 0) return false;
  return true;
}

function allGridInputs() {
  return Array.from(document.querySelectorAll("input")).filter(isGridInput);
}

function clearSelection() {
  document.querySelectorAll(`.${SEL_CLASS}`).forEach((el) => el.classList.remove(SEL_CLASS));
  document.querySelectorAll(`.${ANCHOR_CLASS}`).forEach((el) => el.classList.remove(ANCHOR_CLASS));
}

function getSelected() {
  return Array.from(document.querySelectorAll(`.${SEL_CLASS}`));
}

// Group inputs into visual rows (Y center clustered within ROW_TOLERANCE_PX),
// each row sorted by X center.
function groupIntoRows(inputs) {
  const items = inputs.map((el) => {
    const r = el.getBoundingClientRect();
    return { el, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
  });
  items.sort((a, b) => a.cy - b.cy);
  const rows = [];
  for (const it of items) {
    const last = rows[rows.length - 1];
    if (last && Math.abs(last[0].cy - it.cy) <= ROW_TOLERANCE_PX) {
      last.push(it);
    } else {
      rows.push([it]);
    }
  }
  rows.forEach((row) => row.sort((a, b) => a.cx - b.cx));
  return rows;
}

function highlightRect(anchorEl, currentEl) {
  const a = anchorEl.getBoundingClientRect();
  const c = currentEl.getBoundingClientRect();
  const minX = Math.min(a.left, c.left) - 2;
  const maxX = Math.max(a.right, c.right) + 2;
  const minY = Math.min(a.top, c.top) - 2;
  const maxY = Math.max(a.bottom, c.bottom) + 2;

  const inputs = allGridInputs();
  // Build new set first, then diff against existing class so we don't churn
  // styles on cells already correctly classed.
  const newSet = new Set();
  for (const el of inputs) {
    const r = el.getBoundingClientRect();
    const ecx = r.left + r.width / 2;
    const ecy = r.top + r.height / 2;
    if (ecx >= minX && ecx <= maxX && ecy >= minY && ecy <= maxY) {
      newSet.add(el);
    }
  }
  document.querySelectorAll(`.${SEL_CLASS}`).forEach((el) => {
    if (!newSet.has(el)) el.classList.remove(SEL_CLASS);
  });
  newSet.forEach((el) => el.classList.add(SEL_CLASS));
  document.querySelectorAll(`.${ANCHOR_CLASS}`).forEach((el) => {
    if (el !== anchorEl) el.classList.remove(ANCHOR_CLASS);
  });
  anchorEl.classList.add(ANCHOR_CLASS);
  return newSet.size;
}

function buildTSV() {
  const selected = getSelected();
  if (selected.length === 0) return "";
  const rows = groupIntoRows(selected);
  return rows.map((row) => row.map((it) => it.el.value || "").join("\t")).join("\n");
}

// Set value on a controlled <input> so React's onChange handler fires.
function setInputValue(el, value) {
  const proto = window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

function fallbackCopy(text) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try { document.execCommand("copy"); } catch { /* ignore */ }
  document.body.removeChild(ta);
}

export function useDragRangeSelection({ onCopy, onPaste } = {}) {
  const stateRef = useRef({
    mouseDownAnchor: null,
    isDragging: false,
    movedFarEnough: false,
    startX: 0,
    startY: 0,
    forceSelectionPaste: false,
    pasteAnchor: null,
  });

  useEffect(() => {
    const st = stateRef.current;

    const onMouseDown = (e) => {
      if (e.button !== 0) return; // left button only
      const t = e.target;
      if (!isGridInput(t)) return;
      st.mouseDownAnchor = t;
      st.isDragging = false;
      st.movedFarEnough = false;
      st.startX = e.clientX;
      st.startY = e.clientY;
      // Don't clear selection here — wait until we know if this is click or drag
    };

    const onMouseMove = (e) => {
      if (!st.mouseDownAnchor) return;
      // Cheap distance check to avoid stealing native text selection on tiny moves
      if (!st.movedFarEnough) {
        const dx = e.clientX - st.startX;
        const dy = e.clientY - st.startY;
        if (dx * dx + dy * dy < 16) return; // ~4px threshold
        st.movedFarEnough = true;
      }
      const overEl = document.elementFromPoint(e.clientX, e.clientY);
      const overInput = overEl?.closest("input");
      if (!overInput || !isGridInput(overInput)) return;

      if (!st.isDragging) {
        if (overInput === st.mouseDownAnchor) return; // need to leave anchor to start
        st.isDragging = true;
        try { st.mouseDownAnchor.blur(); } catch { /* ignore */ }
        try { window.getSelection()?.removeAllRanges(); } catch { /* ignore */ }
        document.body.classList.add("cyl-dragging");
      }
      highlightRect(st.mouseDownAnchor, overInput);
      e.preventDefault();
    };

    const onMouseUp = () => {
      if (st.isDragging) {
        st.isDragging = false;
        document.body.classList.remove("cyl-dragging");
      } else if (st.mouseDownAnchor) {
        // Plain click → clear selection
        clearSelection();
      }
      st.mouseDownAnchor = null;
      st.movedFarEnough = false;
    };

    // Shared rectangular paste — given an anchor input + raw TSV text,
    // writes values rectangularly into the visual grid starting at anchor.
    // When `forced` is true (user explicitly selected the destination area),
    // we override the per-input opt-outs that exist for Daily Entry — the
    // user has chosen the target, so respect that. When false (native paste
    // event on a focused input), opt-outs apply so existing per-cell onPaste
    // handlers keep their validation.
    const doRectangularPaste = (anchorEl, text, { forced = false } = {}) => {
      if (!text) return null;
      const lines = text.replace(/\r/g, "").split("\n");
      while (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
      const grid = lines.map((r) => r.split("\t"));
      const rows = grid.length;
      const cols = grid.reduce((m, r) => Math.max(m, r.length), 0);
      if (rows === 0) return null;
      if (!forced && rows <= 1 && cols <= 1) return null; // single value → native

      const inputs = allGridInputs();
      const visualRows = groupIntoRows(inputs);
      let anchorRow = -1, anchorCol = -1;
      for (let i = 0; i < visualRows.length && anchorRow === -1; i++) {
        for (let j = 0; j < visualRows[i].length; j++) {
          if (visualRows[i][j].el === anchorEl) { anchorRow = i; anchorCol = j; break; }
        }
      }
      if (anchorRow === -1) return null;
      let writeCount = 0;
      for (let r = 0; r < rows; r++) {
        const rowList = visualRows[anchorRow + r];
        if (!rowList) break;
        for (let c = 0; c < grid[r].length; c++) {
          const slot = rowList[anchorCol + c];
          if (!slot) continue;
          const inp = slot.el;
          if (inp.readOnly) continue;
          if (!forced) {
            const ids = inp.dataset || {};
            if (
              ids.skipGridPaste === "true" ||
              (ids.gridRow != null && ids.gridCol != null) ||
              (ids.invDenom != null && ids.invField != null) ||
              ids.machineRow != null ||
              ids.salesRow != null
            ) continue;
          }
          setInputValue(inp, grid[r][c]);
          writeCount++;
        }
      }
      return { writeCount, rows, cols };
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (getSelected().length > 0) clearSelection();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "C")) {
        const sel = getSelected();
        if (sel.length <= 1) return; // let native copy of in-cell text fire
        e.preventDefault();
        e.stopPropagation();
        const tsv = buildTSV();
        const reportSuccess = () => { if (onCopy) onCopy(sel.length, tsv); };
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(tsv).then(reportSuccess).catch(() => {
            fallbackCopy(tsv);
            reportSuccess();
          });
        } else {
          fallbackCopy(tsv);
          reportSuccess();
        }
        return;
      }
      // Ctrl/Cmd + V with an ACTIVE drag-selection but no focused grid input:
      // focus the top-left cell of the selection, set a flag so the upcoming
      // native paste event uses FORCED rectangular paste (overrides per-cell
      // opt-outs since the user explicitly selected the destination area).
      // The browser dispatches the paste event AFTER the keydown handlers
      // finish, so focusing inside this handler is enough — no clipboard API
      // permission needed.
      if ((e.ctrlKey || e.metaKey) && (e.key === "v" || e.key === "V")) {
        const sel = getSelected();
        if (sel.length === 0) return; // no selection → let native paste fire on focused input
        const active = document.activeElement;
        if (active && isGridInput(active)) return; // already focused on a grid input → onPasteEvent handles it
        // Find top-left of selection in the visual row grouping
        const inputs = allGridInputs();
        const visualRows = groupIntoRows(inputs);
        let bestRow = Infinity, bestCol = Infinity, anchorEl = null;
        for (const el of sel) {
          for (let i = 0; i < visualRows.length; i++) {
            for (let j = 0; j < visualRows[i].length; j++) {
              if (visualRows[i][j].el === el) {
                if (i < bestRow || (i === bestRow && j < bestCol)) {
                  bestRow = i; bestCol = j; anchorEl = el;
                }
              }
            }
          }
        }
        if (!anchorEl) return;
        st.forceSelectionPaste = true;
        st.pasteAnchor = anchorEl;
        try { anchorEl.focus({ preventScroll: true }); } catch { /* ignore */ }
        // Do NOT preventDefault — browser will now fire the native paste
        // event on the freshly-focused anchor, which onPasteEvent picks up.
      }
    };

    const onPasteEvent = (e) => {
      const target = e.target;
      if (!isGridInput(target)) return;
      // If a selection-driven paste was queued via Ctrl+V keydown, force
      // rectangular paste regardless of per-cell opt-outs (the user
      // explicitly chose the destination area).
      const forced = st.forceSelectionPaste && st.pasteAnchor === target;
      st.forceSelectionPaste = false;
      st.pasteAnchor = null;

      if (!forced) {
        const ds = target.dataset || {};
        if (
          ds.skipGridPaste === "true" ||
          (ds.gridRow != null && ds.gridCol != null) ||
          (ds.invDenom != null && ds.invField != null) ||
          ds.machineRow != null ||
          ds.salesRow != null
        ) return;
      }

      const text = e.clipboardData?.getData("text/plain") || e.clipboardData?.getData("text") || "";
      if (!text) return;
      const lines = text.replace(/\r/g, "").split("\n");
      while (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
      const grid = lines.map((r) => r.split("\t"));
      const rows = grid.length;
      const cols = grid.reduce((m, r) => Math.max(m, r.length), 0);
      // Forced paste: even a single value should fill the whole selection
      // top-left + cascade across visually-grouped destination cells. For
      // event-driven paste, single value → let native paste fill the input.
      if (!forced && rows <= 1 && cols <= 1) return;

      e.preventDefault();
      const result = doRectangularPaste(target, text, { forced });
      if (result && onPaste) onPaste(result.writeCount, result.rows, result.cols);
    };

    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("mousemove", onMouseMove, true);
    document.addEventListener("mouseup", onMouseUp, true);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("paste", onPasteEvent, true);

    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("mousemove", onMouseMove, true);
      document.removeEventListener("mouseup", onMouseUp, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("paste", onPasteEvent, true);
      document.body.classList.remove("cyl-dragging");
      clearSelection();
    };
  }, [onCopy, onPaste]);
}

export default useDragRangeSelection;
